import { Option, Table, TableRow } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { AxiosInstance } from 'axios';
import React, { Dispatch } from 'react';
import {
  Country,
  County,
  DayOfWeek,
  MonthKey,
  MonthlyTimes,
  SchambeckUser,
  TimeTracking,
  WorkSchedule,
  WorkScheduleDistribution,
  convertNumberOfMinutesToTime,
  endAndSaveTimeTracking,
  generateNotification,
  getTimeTrackingsForUserAndDay,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { Right } from 'shared/src/userRole/UserRole.types';
import { uid } from 'uid';
import i18n from '../../i18n/i18n';
import { db } from '../indexedDB/db';
import {
  addTimeTrackingIndexedDB,
  convertIndexedTimeTracking,
  countDbTimeTrackingEntries,
  getIndexedTimeTracking,
} from '../indexedDB/db.utils';
import { isUserAllowedToDo } from '../user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Helper to generate empty Timetracking object with start now
 * @param userId of the user to create timeTracking for
 * @returns created TimeTracking object
 */
export const generateEmptyTimeTracking = (userId: string): TimeTracking => {
  const dateNow = new Date();
  return {
    id: undefined!,
    createDate: dateNow,
    userId,
    startTime: getMinutesOfDay(dateNow),
    breakTime: 0,
    comment: '',
    drivingTimeDeparture: 0,
    drivingTimeArrival: 0,
    endTime: 0,
    startDate: dateNow,
    holidayTime: 0,
    totalWorkTime: 0,
    endDate: new Date(),
  };
};

/**
 * Helper method to get minutes of a day from 0:00 until now
 * @param day with time to check
 * If no day is provided, the actual date from now is used
 * @returns number of minutes
 */
export const getMinutesOfDay = (day?: Date): number => {
  const dayToCount: Date = day || new Date();
  return dayToCount.getHours() * 60 + dayToCount.getMinutes();
};

/**
 * Helper to calculate worktime from start to now
 * @param startTime as minutes of the day
 * @param endTime time to end calculating, default value is now
 * @returns array with hours and minutes
 */
export const calculateWorkingTime = (
  startTime: number,
  endTime: number = getMinutesOfDay(),
): [hours: number, minutes: number] => {
  let hours: number = 0;
  let minutes: number = 0;
  //normal calculation
  if (startTime < endTime) {
    hours = Math.floor((endTime - startTime) / 60);
    minutes = (endTime - startTime) % 60;
  } else {
    //working overnight
    const totalMinutes: number = 24 * 60 - startTime + endTime;
    hours = Math.floor(totalMinutes / 60);
    minutes = totalMinutes % 60;
  }
  return [hours, minutes];
};

/**
 * Helper method to calculate breakTime from start till now
 * The times are 30m between 6 and 9 hours and 45m above 9 hours
 * @param startTime to calculate from
 * @returns  number of minutes of breakTime
 */
export const calculateBreakTime = (startTime: number, endTime?: number): number => {
  const [hours] = calculateWorkingTime(startTime, endTime);
  return hours >= 6 ? (hours >= 9 ? 45 : 30) : 0;
};

/**
 * Helper method to load timeTracking from indexedDB if possible, then chekc mongoDB if nothing was found
 * @param axios instance of axios
 * @param userId id of the user to load the timeTracking for
 * @returns found TimeTracking or undefined
 */
export const getTimeTrackingFromIndexedOrMongoDB = async (
  axios: AxiosInstance,
  userId: string,
): Promise<TimeTracking | undefined> => {
  return getIndexedTimeTracking().then(async (loadedTracking) => {
    if (loadedTracking !== undefined) return convertIndexedTimeTracking(loadedTracking, userId);
    else {
      return await getTimeTrackingsForUserAndDay(axios, userId).then(async (foundTrackings) => {
        const dbEntryAmount: number = await countDbTimeTrackingEntries().then((count) => count);
        foundTrackings.forEach((tracking) => {
          //If endTime is 0, the tracking is not completed yet. If it is not 0, the tracking is completed
          if (tracking?.endTime === 0 && dbEntryAmount === 0) addTimeTrackingIndexedDB(tracking);
        });
        return foundTrackings[0] ? foundTrackings[0] : undefined;
      });
    }
  });
};

/**
 * Util to generate workschedules which distribute the given working time
 * evenly from MO to FR.
 *
 * @param weeklyWorkingTime The user enetered total weekly working time
 * @returns The generated work schedules
 * @tested
 */
export const calculateDailyWorkTimeForWeeklySchedule = (
  weeklyWorkingTime: number,
): WorkSchedule[] => {
  if (weeklyWorkingTime <= 0) return [];
  const dailyWorkTime: number = weeklyWorkingTime / 5;
  return Object.values(DayOfWeek).map((day) => {
    return {
      id: uid(),
      dayOfWeek: day,
      workingHours: day === 'SATURDAY' || day === 'SUNDAY' ? 0 : dailyWorkTime,
    };
  });
};

/**
 * Util to generate WorkSchedules based on the given total weekly working
 * time and the selected days. The total weekly time is evenly divided on
 * the given days.
 *
 * @param weeklyWorkingTime The user entered total weekly working time
 * @param selectedDays The user selected list of selected days
 * @returns The generated work schedules
 * @tested
 */
export const calculateDailyWorkForGivenDays = (
  weeklyWorkingTime: number,
  selectedDays: DayOfWeek[],
): WorkSchedule[] => {
  if (selectedDays.length === 0 || weeklyWorkingTime <= 0) return [];
  const daysToWork: number = selectedDays.length;
  const dailyWorkTime: number = weeklyWorkingTime / daysToWork;
  return Object.values(DayOfWeek).map((day) => {
    return {
      id: uid(),
      dayOfWeek: day,
      workingHours: selectedDays.includes(day) ? dailyWorkTime : 0,
    };
  });
};

/**
 * Util to generate work schedules based on precisely entered daily working hours.
 *
 * @param preciseEntries The user generated precise entries
 * @returns The generated work schedules
 * @tested
 */
export const calculatePreciseWorkScheduleForUser = (
  preciseEntries: Map<DayOfWeek, number>,
): WorkSchedule[] => {
  if (preciseEntries.size <= 0) return [];
  return Object.values(DayOfWeek).map((day) => {
    return {
      id: uid(),
      dayOfWeek: day,
      workingHours: Math.max(preciseEntries.get(day) || 0, 0),
    };
  });
};

/**
 * Helper to map the given user working schedule on the needed Map for the render component
 *
 * @param user The user to create the mapping for
 * @returns Mapping of the precise working schedule
 * @tested
 */
export const generatePreciseMappingFromSchedule = (user: SchambeckUser): Map<DayOfWeek, number> => {
  const returnMap: Map<DayOfWeek, number> = new Map<DayOfWeek, number>();
  if (user.contractInformation?.workScheduleDistribution === WorkScheduleDistribution.PRECISE) {
    user.contractInformation.workSchedules.forEach((schedule) => {
      if (schedule.workingHours <= 0) return;
      returnMap.set(schedule.dayOfWeek, schedule.workingHours);
    });
  }
  return returnMap;
};

/**
 * Helper to calculate the total weekly working time in case the user has a
 * precise schedule mapping. In other cases the already assigned weekly time
 * is returned.
 *
 * @param user The user to calculate the weekly time for
 * @returns The amount of total weekly working time
 * @tested
 */
export const getTotalWorkingTimeFromPreciseMapping = (
  user: SchambeckUser,
  schedules: WorkSchedule[],
): number => {
  if (user.contractInformation?.workScheduleDistribution !== WorkScheduleDistribution.PRECISE)
    return user.contractInformation?.weeklyWorkingTime || 0;
  return schedules.reduce((sum, schedule) => sum + schedule.workingHours, 0);
};

/**
 * Helper method to convert number of minutes to time in the format hh"h" mm"m"
 * @param minutes to convert
 * @returns string in the format hh:mm with correct time
 */
export const convertNumberOfMinutesToString = (minutes: number | undefined): string => {
  if (!minutes) return '0m';
  const fullHoursinMinutes: number = Math.floor(minutes / 60);
  const minutesWithoutHours: number = minutes % 60;

  return fullHoursinMinutes === 0
    ? `${minutesWithoutHours}m`
    : `${fullHoursinMinutes.toLocaleString('de-DE')}h ${
        minutesWithoutHours > 0 ? minutesWithoutHours : minutesWithoutHours * -1
      }m`;
};

/**
 * Util method to generate empty monthly workTimes
 * @returns  empty monthly Worktimes
 */
export const generateEmptyMonthlyWorkTimes = (): MonthlyTimes => ({
  0: 0,
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 0,
  6: 0,
  7: 0,
  8: 0,
  9: 0,
  10: 0,
  11: 0,
});

/**
 * Util method to group the total workTimes of timeTrackings for months
 * @param trackings the trackings to include
 * @returns MonthlyTimes calculated from the trackings
 */
export const getTotalWorkingTimeForMonths = (trackings: TimeTracking[]): MonthlyTimes => {
  const monthlyWorkTimes: MonthlyTimes = generateEmptyMonthlyWorkTimes();
  trackings.forEach((tracking) => {
    const month: MonthKey = tracking.startDate.getMonth().toString() as MonthKey;
    monthlyWorkTimes[month] += tracking.totalWorkTime;
  });
  return monthlyWorkTimes;
};

/**
 * Helper to transform given holidays to Tablerows
 * @param timeTrackings to create rows from
 * @param navigate to create Icons
 * @returns Tablerows
 */
export const convertTimeTrackingsIntoTableEntries = (
  user: SchambeckUser,
  timeTrackings: TimeTracking[],
  setTrackingToEdit: Dispatch<React.SetStateAction<TimeTracking | undefined>>,
  setTrackingToDelete?: Dispatch<string | undefined>,
  readOnly?: boolean,
): TableRow[] => {
  const trackingsPerDay: Map<string, TimeTracking[]> = new Map<string, TimeTracking[]>();
  //Group trackings per day to check if there is jsut one tracking for a day or if there are multiple trackings
  timeTrackings.forEach((tracking) => {
    if (
      trackingsPerDay.has(tracking.startDate.toDateString()) &&
      trackingsPerDay.get(tracking.startDate.toDateString())!.length > 0
    ) {
      const newEntries: TimeTracking[] = [
        ...trackingsPerDay.get(tracking.startDate.toDateString())!,
        tracking,
      ];
      trackingsPerDay.set(tracking.startDate.toDateString(), newEntries);
    } else {
      trackingsPerDay.set(tracking.startDate.toDateString(), [tracking]);
    }
  });
  const dayEntries: [string, TimeTracking[]][] = [...trackingsPerDay.entries()];
  //if there are multiple timetracknigs for that day, we create an expendable row. If there is just 1 entry for the day, we create a normal row
  return dayEntries.map((entry) =>
    entry[1].length === 1
      ? generateRowForSingleTracking(
          user,
          entry[1][0],
          setTrackingToEdit,
          setTrackingToDelete,
          readOnly,
        )
      : generateExpandableRow(user, entry[1], setTrackingToEdit, setTrackingToDelete, readOnly),
  );
};

/**
 * Util method to generate expandable rows for multiple timetrackings on the same day
 * @param user the user that sees the rows, used to check the rights
 * @param trackings the timetrackings to display
 * @param setTrackingToEdit Dispatch to update a tracking
 * @param setTrackingToDelete Dispatch to delete a tracking
 * @returns The expandable TableRow
 */
const generateExpandableRow = (
  user: SchambeckUser,
  trackings: TimeTracking[],
  setTrackingToEdit: Dispatch<React.SetStateAction<TimeTracking | undefined>>,
  setTrackingToDelete?: Dispatch<string | undefined>,
  readOnly?: boolean,
): TableRow => {
  if (!trackings.length) return { id: 'no-content', content: [] };

  const startTrackings: TimeTracking[] = trackings.filter((tracking) => tracking.startTime !== 0);
  let earliestStart: number = 0;
  if (startTrackings.length > 0) {
    const earliestTracking: TimeTracking = startTrackings.sort((trackingOne, trackingTwo) => {
      return adjustTimesForDrivingTime(trackingOne).startTime >
        adjustTimesForDrivingTime(trackingTwo).startTime
        ? 1
        : -1;
    })[0];
    earliestStart = adjustTimesForDrivingTime(earliestTracking).startTime;
  }
  const endTrackings: TimeTracking[] = trackings.filter((tracking) => tracking.endTime !== 1440);
  let latestEnd: number = 0;
  if (endTrackings.length > 0) {
    const latestTracking: TimeTracking = endTrackings.sort((trackingOne, trackingTwo) => {
      return adjustTimesForDrivingTime(trackingOne).endTime <
        adjustTimesForDrivingTime(trackingTwo).endTime
        ? 1
        : -1;
    })[0];

    latestEnd = adjustTimesForDrivingTime(latestTracking).endTime;
  }
  const accumulatedBreakTime: number = trackings.reduce((cur, prev) => cur + prev.breakTime, 0);
  const accumulatedWorkTime: number = calculateMultiTrackingWorkTime(trackings);

  return {
    id: `summary-${trackings[0].startDate}`,
    content: [
      trackings[0].startDate.toLocaleDateString('de-DE'),
      i18n.t(`general.daysOfWeek.${getDayOfWeekForDay(trackings[0].startDate)}`),

      `${convertNumberOfMinutesToTime(earliestStart)}  ${i18n.t('pages.timekeeping.clock')}`,
      `${convertNumberOfMinutesToTime(latestEnd)}  ${i18n.t('pages.timekeeping.clock')}`,
      convertNumberOfMinutesToString(accumulatedBreakTime),
      convertNumberOfMinutesToString(accumulatedWorkTime),
      trackings.map((tracking) => tracking.comment).join(', '),
    ],
    actions: [],
    children: (
      <Table
        hidePagination
        headless
        hideTopFilter
        header={
          i18n.t('pages.timekeeping.tableHeader', {
            returnObjects: true,
          }) as TableHeader[]
        }
        rows={trackings.map((tracking) =>
          generateRowForSingleTracking(
            user,
            tracking,
            setTrackingToEdit,
            setTrackingToDelete,
            readOnly,
          ),
        )}
      />
    ),
  };
};

/**
 * Util method to calculate the workTime for multiple trackings without overlap
 * @param trackings the trackings to calculate the time for
 * @returns number of minutes of combined workTime
 * @tested
 */
export const calculateMultiTrackingWorkTime = (trackings: TimeTracking[]): number => {
  const sortedTrackings: TimeTracking[] = trackings.sort(
    (trackingA, trackingB) => trackingA.startTime - trackingB.startTime,
  );
  const periods: number[][] = sortedTrackings.map((tracking) => [
    tracking.startTime - tracking.drivingTimeArrival,
    tracking.endTime + tracking.drivingTimeDeparture,
  ]);
  let total: number = 0;
  let currentStart: number = periods[0][0];
  let currentEnd: number = periods[0][1];

  sortedTrackings.forEach((tracking, index) => {
    const [start, end] = periods[index];
    if (start <= currentEnd) {
      if (end > currentEnd) {
        total -= tracking.breakTime;
      }
      // Overlapping → extend the current interval
      currentEnd = Math.max(currentEnd, end);
    } else {
      // No overlap → add current length
      total += currentEnd - currentStart;
      currentStart = start;
      currentEnd = end;
      total -= tracking.breakTime;
    }
  });
  // Add the final interval
  total += currentEnd - currentStart - sortedTrackings[0].breakTime;
  return total;
};

/**
 * Util method to get the correct dayOfWeek for a date
 * @param day the date to get the DayOfWeek for
 * @returns the correct dayOfWeek for the date
 */
export const getDayOfWeekForDay = (date: Date): DayOfWeek | undefined => {
  switch (date.getDay()) {
    case 0:
      return DayOfWeek.SUNDAY;
    case 1:
      return DayOfWeek.MONDAY;
    case 2:
      return DayOfWeek.TUESDAY;
    case 3:
      return DayOfWeek.WEDNESDAY;
    case 4:
      return DayOfWeek.THURSDAY;
    case 5:
      return DayOfWeek.FRIDAY;
    case 6:
      return DayOfWeek.SATURDAY;
  }
};

/**
 * Util method to create a TableRow for a single timeTracking
 * @param user the user that sees the rows, used to check the rights
 * @param tracking the tracking to generate the row for
 * @param setTrackingToEdit Dispatch to update a tracking
 * @param setTrackingToDelete Dispatch to delete a tracking
 * @returns The TableRow containing the timeTracking
 */
const generateRowForSingleTracking = (
  user: SchambeckUser,
  tracking: TimeTracking,
  setTrackingToEdit: Dispatch<React.SetStateAction<TimeTracking | undefined>>,
  setTrackingToDelete?: Dispatch<string | undefined>,
  readOnly?: boolean,
): TableRow => {
  const { startTime, endTime } = adjustTimesForDrivingTime(tracking);
  return {
    id: tracking.id,
    content: [
      tracking.startDate.toLocaleDateString('de-DE'),
      i18n.t(`general.daysOfWeek.${getDayOfWeekForDay(tracking.startDate)}`),
      `${convertNumberOfMinutesToTime(startTime)}  ${i18n.t('pages.timekeeping.clock')}`,
      `${convertNumberOfMinutesToTime(endTime)}  ${i18n.t('pages.timekeeping.clock')}`,
      convertNumberOfMinutesToString(tracking.breakTime),
      convertNumberOfMinutesToString(tracking.totalWorkTime),
      tracking.comment,
    ],
    actions: [
      <div>
        {(isUserAllowedToDo(user.right, Right.TIME_EDIT) || user.id === tracking.userId) && (
          <div className="table-action__icon-wrapper">
            {readOnly ? (
              <></>
            ) : (
              <>
                <IconComponent
                  type={IconType.EDIT}
                  width={20}
                  onClick={() => setTrackingToEdit(tracking)}
                />
                {setTrackingToDelete && (
                  <IconComponent
                    type={IconType.DELETE}
                    className="delete"
                    width={20}
                    onClick={() => setTrackingToDelete(tracking.id)}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>,
    ],
  };
};

/**
 * Util method to adjust the start and endTime for timetrackings in regard of the driving time
 * @param tracking the tracking to get the adjsuted times for
 * @returns start and endTime with the added drivingTime
 * @tested
 */
export const adjustTimesForDrivingTime = (
  tracking: TimeTracking,
): { startTime: number; endTime: number } => {
  let startTime: number = tracking.startTime - tracking.drivingTimeArrival;
  let endTime: number = tracking.endTime + tracking.drivingTimeDeparture;

  //If driving time adjusted startTime is below 0, we add the driving time to the end
  if (startTime < 0) {
    endTime += startTime * -1;
    startTime = 0;
  }
  //If driving time adjusted endTime is over 24:00, we add the driving time to the start
  if (endTime > 1440) {
    startTime -= endTime - 1440;
    endTime = 1440;
  }
  return { startTime, endTime };
};

/**
 * Helper method to create options for the dropdown to choose a year
 * @returns array of options with label and value
 */
export const generateDropdownOptionsForYear = (): Option[] => {
  const yearNow: number = new Date().getFullYear();
  const options: Option[] = [];
  for (let i = yearNow - 10; i <= yearNow + 1; i++) {
    options.push({ label: i, value: i.toString() });
  }
  options.sort((a, b) => (Number(a.value) >= Number(b.value) ? -1 : 1));
  return options;
};
/**
 * Helper method to create options for the dropdown to choose a month
 * @returns array of options with label and value
 */
export const generateDropdownOptionsForMonth = (): Option[] => {
  const options: Option[] = [];
  for (let curMonth = 0; curMonth <= 11; curMonth++) {
    const optionDate: Date = new Date();
    optionDate.setMonth(curMonth);
    options.push({
      label: i18n.t(`general.month.${curMonth}`),
      value: curMonth.toString(),
    });
  }

  return options;
};

/**
 * Util method to generate dropdown options for countries
 * @returns array of dropdown options
 */
export const generateDropdownOptionsForCountry = (): Option[] => {
  return Object.values(Country).map((country) => ({
    label: i18n.t(`general.country.${country}`),
    value: country,
  }));
};

/**
 * Util method to generate dropdown options for counties
 * @returns array of dropdown options
 */
export const generateDropdownOptionsForCounty = (): Option[] => {
  return Object.values(County)
    .filter((county) => county !== County.OTHER)
    .map((county) => ({
      label: i18n.t(`general.county.${county}`),
      value: county,
    }));
};

/**
 * Util method to convert a time string from the format hh : mm or hh:mm to the number of minutes from 00:00.
 * @param timeString to convert
 * @returns number of total minutes
 */
export const getMinutesFromTimeString = (timeString: string): number => {
  const times: string[] = timeString.split(':');
  const timesWithoutSpaces: string[] = [times[0].replace(/\s/g, ''), times[1].replace(/\s/g, '')];
  const numberOfMinutes: number =
    Number(timesWithoutSpaces[0]) * 60 + Number(timesWithoutSpaces[1]);
  return numberOfMinutes;
};

/**
 * Helper method to get the correct display text to display for the actual timeTracking of the day
 * depending on the activeTracking startTime, endTime and totalWorkTime
 * @returns string to display
 */
export const getCorrectDisplayText = (activeTracking?: TimeTracking): string => {
  if (!activeTracking) return i18n.t('pages.timekeeping.noActiveTracking');
  else if (activeTracking.endTime === 0)
    return `${i18n.t('pages.timekeeping.start')}: ${convertNumberOfMinutesToTime(
      activeTracking.startTime,
    )} ${i18n.t('pages.timekeeping.clock')}`;
  else
    return `${i18n.t('pages.timekeeping.trackedTime')}: ${convertNumberOfMinutesToTime(
      activeTracking.totalWorkTime,
    )}`;
};

/**
 * Util method to end a timeTracking with the actual time, that updates the tracking on the database and clears the indexedDB
 * @param userId id of the user the timetracking is for
 * @param activeTracking tracking to update and end
 * @param axios instance of axios
 * @returns updated tracking or undefined on error
 */
export const endTimeTracking = async (
  userId: string,
  activeTracking: TimeTracking,
  axios: AxiosInstance,
): Promise<TimeTracking | undefined> => {
  const workTimeNow: number[] = calculateWorkingTime(activeTracking.startTime);
  const today: Date = new Date();
  const updatedTracking: TimeTracking = {
    ...activeTracking,
    endDate: today,
    userId: userId,
    endTime: getMinutesOfDay(today),
    totalWorkTime: workTimeNow[0] * 60 + workTimeNow[1] - activeTracking.breakTime,
  };

  if (updatedTracking.totalWorkTime <= 0) {
    generateNotification({
      type: NotificationType.WARNING,
      value: i18n.t('general.notification.error.timeTrackingTimes'),
    });
    return;
  }
  await endAndSaveTimeTracking(axios, updatedTracking);
  await db.timeTracking.clear();
  return updatedTracking;
};
